import { useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../ui/input";
import SpecTemplateDrop from "./SpecTemplateDrop";
import SpecDelta from "./SpecDelta";

/** Specification-template intake inside an existing project (HITL). */
export default function SpecTemplatePanel({ projectId, onApplied }) {
  const [tpl, setTpl] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [custom, setCustom] = useState({});
  const [saveTpl, setSaveTpl] = useState(true);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const onResult = (r) => {
    setTpl(r);
    setName(r.template?.name || "Specification template");
    setAccepted(Object.fromEntries((r.rows || [])
      .filter((x) => x.field_id && ["mapped", "converted"].includes(x.status))
      .map((x) => [x.field_id, true])));
    setCustom({});
  };

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/spec-template/commit`, {
        staging_token: tpl.staging_token, rows: tpl.rows,
        accepted: Object.keys(accepted).filter((k) => accepted[k]),
        add_custom: Object.keys(custom).filter((k) => custom[k]).map(Number),
        save_template: saveTpl, template_name: name,
      });
      toast.success(`${data.applied} parameter(s) written to the specification`
        + (data.custom_added ? `, ${data.custom_added} new data-model field(s)` : ""));
      onApplied?.();
      setTpl(null);
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="spec-template-panel">
      <SpecTemplateDrop onResult={onResult} disabled={busy} />
      {tpl && (
        <>
          <SpecDelta result={tpl} accepted={accepted} custom={custom}
            onToggle={(fid, on) => setAccepted((a) => ({ ...a, [fid]: on }))}
            onToggleCustom={(rowNo, on) => setCustom((c) => ({ ...c, [rowNo]: on }))}
            onEdit={(rowNo, val) => setTpl((t) => ({
              ...t, rows: t.rows.map((r) => (r.row === rowNo ? { ...r, value: val } : r)) }))} />
          <div className="flex flex-wrap items-center gap-3 rounded-sm border border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-2.5">
            <label className="flex items-center gap-2 text-[11px] text-[var(--c-text)] cursor-pointer">
              <input type="checkbox" className="accent-[var(--c-accent)]" checked={saveTpl}
                data-testid="stp-save-toggle" onChange={(e) => setSaveTpl(e.target.checked)} />
              Save this mapping for reuse
            </label>
            <Input data-testid="stp-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Template name"
              className="h-8 flex-1 min-w-[12rem] text-xs bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)]" />
            <button data-testid="stp-apply" onClick={apply} disabled={busy}
              className="h-8 px-3 bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white text-xs rounded-sm disabled:opacity-50">
              {busy ? "Applying…" : "Apply to specification"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
